import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom/client';
import { Route, Routes, useNavigate } from 'react-router-dom';
import moment from 'moment';
// These are no longer needed with direct Route usage
// import PrivateRoute from './components/Routes/PrivateRoute';
// import PublicRoute from './components/Routes/PublicRoute';
import PlanInfo from 'components/Common/PlanInfo';
import Typography from 'components/Atoms/Typography';

import { useLocation } from 'react-router-dom';
import pathToRegexp from 'path-to-regexp';

import { Path, ShowBookDemoButtonRoutes } from 'constants/routes';

import { FF_INSIGHTS } from 'constants/feature-flags';

import './assets/styles/routes.css';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { SideBarMenuClasses } from 'constants/common';
import { useDispatch, useSelector } from 'react-redux';
import {
  hideSupportIconRecursively,
  showSupportIcon,
} from 'utils/handleSupportIconVisibility';
import { sendAmplitudeEvents } from 'utils/amplitude';
import { clearJobList } from 'store/actions/hypertestJobList';
import { isOnPremURL } from 'utils/brandUrl';
import PrivateRoute from 'components/Routes/PrivateRoute';

const PRIVATE_TYPE = 'private';
const PUBLIC_TYPE = 'public';

const SIDEBAR_HIDDEN_CLASS_NAME = 'ltch-hidden';

// const ConfigureTest = React.lazy(() =>
//   import('./components/Onboarding/ConfigureTest')
// );

// const ConfigureTestYamlBased = React.lazy(() =>
//   import('./components/Onboarding/ConfigureTestYamlBased')
// );

// No longer needed with React Router v6
// const ExcludeExactMatchRoutes = [Path.HYPEREXECUTE, Path.JOBS];

const UrlToClassMapping = {
  [Path.JOBS]: SideBarMenuClasses.JOBS,
  [Path.TASK]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START_CONFIGURE_TEST.Path]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START_QUICK_RUN.Path]: SideBarMenuClasses.JOBS,
  [Path.QUICK_START_YAML_BASED_TEST.Path]: SideBarMenuClasses.JOBS,
  [Path.ONBOARD]: SideBarMenuClasses.JOBS,
  [Path.PROJECT_LISTING]: SideBarMenuClasses.PROJECTS,
  [Path.PROJECT_DETAILS]: SideBarMenuClasses.PROJECTS,
  [Path.WORKFLOW]: SideBarMenuClasses.WORKFLOW,
  [Path.INSIGHTS.Path]: SideBarMenuClasses.INSIGHTS,
  [Path.INSIGHTS_OVERVIEW]: SideBarMenuClasses.INSIGHTS,
};
const SideNavData = {
  Jobs: {
    CLASS_NAME: SideBarMenuClasses.JOBS,
    PATH: Path.JOBS,
  },
  Projects: {
    CLASS_NAME: SideBarMenuClasses.PROJECTS,
    PATH: Path.PROJECT_LISTING,
    show: true,
  },
  // Workflow: {
  //   CLASS_NAME: SideBarMenuClasses.WORKFLOW,
  //   PATH: Path.WORKFLOW,
  //   FEATURE_FLAG: FF_WORKFLOW,
  // },
  // Insights: {
  //   CLASS_NAME: SideBarMenuClasses.INSIGHTS,
  //   PATH: Path.INSIGHTS_OVERVIEW,
  //   FEATURE_FLAG: FF_INSIGHTS,
  // },
};

const RoutesMain = ({ children }) => {
  const featureFlags = useFeatureFlags();
  const visibleMenusList = getMenusToShow();
  const enabledMenusRef = useRef(visibleMenusList);
  const retryCount = useRef(0);
  const userDataRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const loactionRef = useRef(location);
  const userInfo = useSelector((state) => state.userInfo?.userInfo);
  const userInfoVisual = useSelector((state) => state.userInfo?.visual);
  const filters = useSelector((state) => state.filters?.filters);
  const filterRef = useRef(null);

  const isRouteChangedRef = useRef(false);
  // const userInfoVisual = useSelector((state) => state.userInfo?.visual);

  const dispatch = useDispatch();

  const isLoadingFeatureFlags = useSelector(
    (state) => state?.featureFlag?.isLoading
  );

  const { data: userInfoData } = userInfo;
  const isTrialUser = userInfoVisual.isTrialUser;

  const navItemRetryCount = useRef(0);

  enabledMenusRef.current = visibleMenusList;
  filterRef.current = filters;
  userDataRef.current = userInfoData;

  function handleChatLoadedEvent() {
    const elem = document.getElementById('embedded-messaging');
    if (isTrialUser && !getMatchingRoute()) {
      elem?.classList?.add('lt-new-chat-bot');
    } else {
      elem?.classList?.remove('lt-new-chat-bot');
    }
  }

  function getMatchingRoute() {
    return ShowBookDemoButtonRoutes.find((route) => {
      const regExp = pathToRegexp(route);
      return regExp.test(location.pathname);
    });
  }

  useEffect(() => {
    if (isTrialUser) {
      window.addEventListener('LTCH_SFChatBotLoaded', handleChatLoadedEvent);

      if (getMatchingRoute()) {
        hideSupportIconRecursively();
        typeof window?.renderBookADemoBtn === 'function' &&
          window?.renderBookADemoBtn('hyperexecute', {}, true);
        setTimeout(() => {
          const btn = document.getElementsByClassName(
            'ltch-book-a-demo-btn'
          )[0];
          if (btn) {
            hideSupportIconRecursively();
            btn.removeEventListener('click', handleBookADemoClick);
            setTimeout(() => {
              btn.addEventListener('click', handleBookADemoClick);
            }, 10);
          }
        }, 800);
      } else {
        const btn = document.getElementsByClassName('ltch-book-a-demo-btn')[0];
        if (btn) {
          btn.removeEventListener('click', handleBookADemoClick);
        }
        showSupportIcon();
        typeof window?.hideBookADemoBtn === 'function' &&
          window?.hideBookADemoBtn();
      }
    }

    return () => {
      window.removeEventListener('LTCH_SFChatBotLoaded', handleChatLoadedEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname, isTrialUser]);

  function handleBookADemoClick() {
    sendAmplitudeEvents('Clicked : book a demo', { product: 'hyperexecute' });
  }

  function getMenusToShow() {
    const arr = [];
    const sideBarMenus = getSidebarNavData();
    Object.keys(sideBarMenus).forEach((key) => {
      const data = sideBarMenus[key];
      if ((data.FEATURE_FLAG && featureFlags[data.FEATURE_FLAG]) || data.show) {
        arr.push(data);
      }
    });
    return arr;
  }

  function handleActiveClass(activeClass) {
    const subNavClasses = Object.values(SideBarMenuClasses);
    subNavClasses.forEach((classValue) => {
      const ele = document.getElementsByClassName(classValue)[0];
      if (classValue !== activeClass) {
        ele?.classList?.remove('active');
      } else {
        ele?.classList?.add('active');
      }
    });
  }
  function getCurrentFilterURLUsingFilterReducers() {
    const jobFilters = filterRef?.current?.jobFilters;
    const enabledFilters = filterRef?.current?.enabledFilters;
    let lambdaTestFilterObject = jobFilters;
    let queryString = '';
    if (lambdaTestFilterObject) {
      Object.keys(lambdaTestFilterObject).forEach(function (k) {
        if (lambdaTestFilterObject[k]) {
          if (
            lambdaTestFilterObject[k] instanceof Array &&
            enabledFilters.includes(k)
          ) {
            if (k === 'range') {
              if (lambdaTestFilterObject[k].length > 0) {
                let dataRange = lambdaTestFilterObject[k][0];
                let startDate = moment(dataRange.startDate).format(
                  'YYYY-MM-DD'
                );
                let endDate = moment(dataRange.endDate).format('YYYY-MM-DD');
                queryString = `${queryString}&${k}=${startDate},${endDate}`;
              }
            } else {
              if (lambdaTestFilterObject[k].length > 0) {
                queryString = `${queryString}&${k}=${lambdaTestFilterObject[
                  k
                ].join(',')}`;
              }
            }
          }
          if (
            typeof lambdaTestFilterObject[k] === 'string' ||
            lambdaTestFilterObject[k] instanceof String
          ) {
            if (lambdaTestFilterObject[k].trim() !== '') {
              queryString = `${queryString}&${k}=${lambdaTestFilterObject[k]}`;
            }
          }
        }
      });
    }
    return queryString;
  }

  function handleNavItemClick(pathName, activeClass) {
    return (e) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (
        pathName === Path.JOBS &&
        !window.location.pathname.includes('jobs')
      ) {
        const filterURL = getCurrentFilterURLUsingFilterReducers();
        dispatch(clearJobList());
        navigate(`${Path.JOBS}?${filterURL}`);
      } else if (
        window.location.pathname === Path.PROJECT_DETAILS &&
        pathName === Path.PROJECT_LISTING
      ) {
        navigate(pathName);
      } else {
        !window.location.pathname.includes(pathName) && navigate(pathName);
      }
      handleActiveClass(activeClass);
    };
  }

  function handleNavigation() {
    const sideBarMenus = getSidebarNavData();
    const menus = Object.values(sideBarMenus);
    for (let i = 0; i < menus.length; i++) {
      let ele = document.getElementsByClassName(menus[i].CLASS_NAME)[0];
      if (!ele && navItemRetryCount.current < 20) {
        navItemRetryCount.current = navItemRetryCount.current + 1;
        setTimeout(() => {
          handleNavigation();
        }, 10);
        break;
      }
      ele?.removeEventListener(
        'click',
        handleNavItemClick(menus[i].PATH, menus[i].CLASS_NAME)
      );
      ele?.addEventListener(
        'click',
        handleNavItemClick(menus[i].PATH, menus[i].CLASS_NAME)
      );
    }
  }

  function handleProductNavExpanded() {
    retryCount.current = 0;
    navItemRetryCount.current = 0;
    handleNavigation();
    showMenuInSidebar();
  }

  useEffect(() => {
    loactionRef.current = location;

    if (location?.pathname?.includes('project')) {
      window.isProjectListPage = true;
    } else if (location?.pathname?.includes('job')) {
      window.isProjectListPage = false;
    }
    if (isRouteChangedRef.current) {
      window.sessionStorage.setItem('back_action', 'pop');
    } else {
      isRouteChangedRef.current = true;
    }
  }, [location]);

  useEffect(() => {
    window.addEventListener('productNavExpanded', handleProductNavExpanded);
    return () => {
      retryCount.current = 0;
      navItemRetryCount.current = 0;
      window.removeEventListener('productNavExpanded', () => { });
      const sidebarMenus = getSidebarNavData();
      const menus = Object.values(sidebarMenus);
      for (let i = 0; i < menus.length; i++) {
        let ele = document.getElementsByClassName(menus[i].CLASS_NAME)[0];
        ele?.removeEventListener(
          'click',
          handleNavItemClick(menus[i].PATH, menus[i].CLASS_NAME)
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getSidebarNavData() {
    const isOnPrem = isOnPremURL();
    const list = structuredClone(SideNavData);
    if (isOnPrem) {
      delete list.Workflow;
      delete list.Insights;
    }

    return list;
  }

  useEffect(() => {
    if (
      !isLoadingFeatureFlags ||
      (isOnPremURL() && featureFlags.normalFFFetched)
    ) {
      setTimeout(() => {
        retryCount.current = 0;
        showMenuInSidebar();
      }, 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isLoadingFeatureFlags, featureFlags]);

  useEffect(() => {
    navItemRetryCount.current = 0;
    handleNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function showMenuInSidebar() {
    for (let i = 0; i < enabledMenusRef.current.length; i++) {
      const value = enabledMenusRef.current[i];
      const ele = document.getElementsByClassName(value.CLASS_NAME)[0];

      if (!ele && retryCount.current < 50) {
        retryCount.current = retryCount?.current + 1;
        setTimeout(() => {
          showMenuInSidebar();
        }, 100);
        break;
      } else if (ele) {
        ele.classList.remove(SIDEBAR_HIDDEN_CLASS_NAME);
        window.setTimeout(() => {
          let activeClass = UrlToClassMapping[loactionRef.current?.pathname];
          if (!activeClass) {
            let baseLocationValue = loactionRef.current?.pathname.split('/');
            baseLocationValue.pop();
            activeClass = UrlToClassMapping[baseLocationValue?.join('/')];
          }
          handleActiveClass(activeClass);
        }, 2);
      }
    }

    const sideBarMenus = getSidebarNavData();
    Object.keys(sideBarMenus).forEach(function (key) {
      const nav = sideBarMenus[key];
      if (!nav?.PATH?.includes('job')) {
        let isShown = false;
        for (let i = 0; i < enabledMenusRef?.current?.length; i++) {
          const value = enabledMenusRef?.current[i];
          isShown = value?.PATH === nav?.PATH;
          if (isShown) {
            break;
          }
        }

        if (!isShown) {
          const ele = document.getElementsByClassName(nav.CLASS_NAME)[0];
          if (ele) {
            ele?.classList?.add(SIDEBAR_HIDDEN_CLASS_NAME);
          }
        }
      }
    });
  }

  return <>{children}</>;
};

// Main component that renders the routes
const RoutesWrapper = () => {
  const [routesList, setRouteList] = useState([]);
  const [showRoutes, setShowRoutes] = useState(false);

  useEffect(() => {
    (async function () {
      let list = [];
      list = (await import('./newThemeRoutes')).default;
      setRouteList(list);
      setShowRoutes(true);
    })();
  }, []);

  if (!showRoutes) {
    return null;
  }

  return (
    <>
      <HeaderInfo />
      <RoutesMain>
        <Routes>
          {routesList?.map(({ path, key, Component, type, ...props }) => {
            if (type === PUBLIC_TYPE)
              return (
                <Route
                  key={key || path}
                  path={path}
                  element={<Component />}
                  {...props}
                />
              );
            if (type === PRIVATE_TYPE)
              return (
                <Route
                  key={key || path}
                  path={path}
                  element={
                    <PrivateRoute path={path}>
                      <Component {...props} />
                    </PrivateRoute>
                  }
                  {...props}
                />
              );
            return path;
          })}
        </Routes>
      </RoutesMain>
    </>
  );
};

function HeaderInfo() {
  const [is, setIs] = useState(false);

  useEffect(() => {
    function check() {
      var ele = document.getElementsByClassName('navbar__header')[0];
      if (!ele) {
        setTimeout(check, 10);
      } else {
        setIs(true);
      }
    }

    check();
  }, []);

  if (!is) {
    return null;
  }

  var ele = document.getElementsByClassName('navbar__header')[0];
  if (!ele) {
    return null;
  }
  return createPortal(
    <div className="common-header-hyp-info">
      <Typography variant="h1" extraClasses="common-header-title">
        HyperExecute
      </Typography>
      <PlanInfo />
    </div>,
    ele
  );
}

export default RoutesWrapper;
